import React from "react";

import { useTheme } from "@mui/material/styles";
import { Box, Tooltip, Typography, Stack } from "@mui/material";

import icn_cooper from "assets/images/icons/icn_copper_disconnected.svg";
import icn_cooper_1g from "assets/images/icons/icn_copper_1gbps.svg";
import icn_cooper_25_5g from "assets/images/icons/icn_copper_25_5g.svg";
import icn_cooper_10g from "assets/images/icons/icn_copper_10g.svg";
import icn_cooper_25g from "assets/images/icons/icn_copper_25g.svg";
import icn_cooper_40g from "assets/images/icons/icn_copper_40g.svg";
import icn_cooper_100g from "assets/images/icons/icn_copper_100g.svg";
import icn_cooper_disconnected from "assets/images/icons/icn_copper_disconnected-2.svg";
import icn_cooper_disabled from "assets/images/icons/icn_copper_disabled.svg";
import icn_cooper_error from "assets/images/icons/icn_copper_error.svg";

import icn_fiber from "assets/images/icons/icn_fiber_black.svg";
import icn_fiber_1g from "assets/images/icons/icn_fiber_1g.svg";
import icn_fiber_25_5g from "assets/images/icons/icn_fiber_5g.svg";
import icn_fiber_10g from "assets/images/icons/icn_fiber_10g.svg";
import icn_fiber_25g from "assets/images/icons/icn_fiber_25g.svg";
import icn_fiber_40g from "assets/images/icons/icn_fiber_40g.svg";
import icn_fiber_100g from "assets/images/icons/icn_fiber_100g.svg";
import icn_fiber_disconnected from "assets/images/icons/icn_fiber_disconnected.svg";
import icn_fiber_disabled from "assets/images/icons/icn_fiber_disabled.svg";
import icn_fiber_error from "assets/images/icons/icn_fiber_error.svg";

import icn_10_100mbps from "assets/images/icons/icn_copper_10_100mbps.svg";
import icn_port_uplink from "assets/images/icons/icn_port_uplink.svg";
import icn_port_poe_30w from "assets/images/icons/icn_port_poe.svg";
import icn_port_poe_60w from "assets/images/icons/icn_port_poe++.svg";
import icn_port_poe_90w from "assets/images/icons/icn_port_poe+++.svg";

const OverlapIcons = ({ images = [], size = 24, offsets = [] }) => (
  <Box sx={{ position: "relative", width: size, height: size }}>
    {images.map((imgSrc, index) => (
      <Box
        key={index}
        component="img"
        src={imgSrc}
        alt="-"
        sx={{
          position: "absolute",
          top: offsets[index] || 0,
          left: offsets[index] || 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    ))}
  </Box>
);

const TooltipInfo = ({ imgSrc, label }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="img"
        src={imgSrc}
        alt="-"
        sx={{ width: 20, height: 20, objectFit: "contain" }}
      />
      <Typography
        sx={{
          fontSize: theme.typography.customFontSize[0],
          fontWeight: theme.typography.customFontWeight[0],
          color: theme.palette.primary.white,
          lineHeight: 1.33
        }}>
        {label}
      </Typography>
    </Stack>
  )
};

const PortTooltipContent = ({ result, portStatusCondition, portStatusLabel }) => {
  if (!result) return null;

  if (Array.isArray(result)) {
    return (
      <Stack spacing={1} direction="row">
        {result.map((imgSrc, index) => {
          const key = Object.entries(portStatusCondition).find(([, value]) => value === imgSrc)[0];
          return (
            <TooltipInfo
              key={index}
              imgSrc={imgSrc}
              label={portStatusLabel[key]}
            />
          );
        })}
      </Stack>
    );
  }
  return (
    <TooltipInfo
      imgSrc={portStatusCondition[result]}
      label={portStatusLabel[result]}
    />
  );
};

const portStatusCondition = {
  cooper: icn_cooper,
  cooper_1g: icn_cooper_1g,
  cooper_25_5g: icn_cooper_25_5g,
  cooper_10g: icn_cooper_10g,
  cooper_25g: icn_cooper_25g,
  cooper_40g: icn_cooper_40g,
  cooper_100g: icn_cooper_100g,
  cooper_disconnected: icn_cooper_disconnected,
  cooper_disabled: icn_cooper_disabled,
  cooper_error: icn_cooper_error,
  fiber: icn_fiber,
  fiber_1g: icn_fiber_1g,
  fiber_25_5g: icn_fiber_25_5g,
  fiber_10g: icn_fiber_10g,
  fiber_25g: icn_fiber_25g,
  fiber_40g: icn_fiber_40g,
  fiber_100g: icn_fiber_100g,
  fiber_disconnected: icn_fiber_disconnected,
  fiber_disabled: icn_fiber_disabled,
  fiber_error: icn_fiber_error,
  speed_10_100mbps: icn_10_100mbps,
  port_uplink: icn_port_uplink,
  port_poe_30w: icn_port_poe_30w,
  port_poe_60w: icn_port_poe_60w,
  port_poe_90w: icn_port_poe_90w,
};

export const portStatusLabel = {
  cooper: "Copper",
  cooper_1g: "1 Gbps",
  cooper_25_5g: "2.5/5 Gbps",
  cooper_10g: "10 Gbps",
  cooper_25g: "25 Gbps",
  cooper_40g: "40 Gbps",
  cooper_100g: "100 Gbps",
  cooper_disconnected: "Disconnected",
  cooper_disabled: "Disabled",
  cooper_error: "Error",
  fiber: "Fiber",
  fiber_1g: "1 Gbps",
  fiber_25_5g: "2.5/5 Gbps",
  fiber_10g: "10 Gbps",
  fiber_25g: "25 Gbps",
  fiber_40g: "40 Gbps",
  fiber_100g: "100 Gbps",
  fiber_disconnected: "Disconnected",
  fiber_disabled: "Disabled",
  fiber_error: "Error",
  speed_10_100mbps: "10/100 Mbps",
  port_uplink: "Uplink",
  port_poe_30w: "PoE/PoE+",
  port_poe_60w: "PoE++ (60W)",
  port_poe_90w: "PoE++ (90W)",
};

export function getSwitchPortStatusLabel(port) {
  let label = "-";

  const result = getSwitchPortStatusImgSrc(port);
  if (!result) return label;

  if (Array.isArray(result)) {
    // PoE Label
    const key = Object.entries(portStatusCondition).find(([, v]) => v === result[0])?.[0];
    label = key ? portStatusLabel[key] : "-";
  }else{
    // Not PoE Label.
    label = portStatusLabel[result] ?? "-";
  }

  return label;
}

export function getSwitchPortStatusImgSrc(port) {
  if (!port) return null;

  let result;
  const mediaType = port.portStaMediaType === 1 ? "cooper" : port.portStaMediaType === 2 ? "fiber" : null;
  if (!mediaType) return null;

  // 判斷 沒有接線(斷線) / 停用(disabled) / 錯誤 狀態
  if ((port.portAttriAdminState === 0) || port.portAttriAdminState === 1 && (port.portStaStatus === 0 || port.portStaStatus === 2)) result = `${mediaType}_disconnected`;
  if (port.portAttriAdminState === 2) result = `${mediaType}_disabled`;

  // 判斷 uplink
  if (port.portStaErrorState !== "00") result = `${mediaType}_error`;

  // 速度判斷
  if (port.portAttriAdminState === 1 && port.portStaErrorState === "00" && port.portStaStatus === 1) {
    // 判斷 速度
    const speed = port.portStaSpeed;
    if ([1, 2, 3].includes(speed)) result = "speed_10_100mbps";
    if ([4, 5, 6].includes(speed)) result = `${mediaType}_1g`;
    if ([7, 8].includes(speed)) result = `${mediaType}_25_5g`;
    if (speed === 9) result = `${mediaType}_10g`;
    if (speed === 11) result = `${mediaType}_40g`;
    if (speed === 12) result = `${mediaType}_100g`;
  }

  // 判斷 uplink ( Issue 131 : Hide uplink temporarily )
  // if (port.portStaIsUplink === 1) {
  //     const baseImage = portStatusCondition[result];
  //     const uplinkImage = portStatusCondition["port_uplink"];
  //     result = [baseImage, uplinkImage];
  // }

  // 判斷 PoE
  if (port.poePortStatus === 3) {
    const poeImages = [];
    if ([0, 1, 2, 3, 4].includes(port.poePortClassifications)) poeImages.push(portStatusCondition["port_poe_30w"]);
    if ([5, 6].includes(port.poePortClassifications)) poeImages.push(portStatusCondition["port_poe_60w"]);
    if ([7, 8].includes(port.poePortClassifications)) poeImages.push(portStatusCondition["port_poe_90w"]);

    result = Array.isArray(result)
      ? [...result, ...poeImages]
      : [portStatusCondition[result], ...poeImages];
  }

  return result ?? null;
}

function getSwitchPortStatus(port) {
  const result = getSwitchPortStatusImgSrc(port);
  if (!result) return null;

  return (
    <Tooltip
      title={
        <PortTooltipContent
          result={result}
          portStatusCondition={portStatusCondition}
          portStatusLabel={portStatusLabel}
        />
      }
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "primary.greyPrimaryTransparency",
            borderRadius: 1,
            p: 1,
            maxWidth: "none",
            width: "auto",
            height: "auto",
          },
        },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center" }}>
        {Array.isArray(result) ? (
          <OverlapIcons images={result} />
        ) : (
          <Box
            component="img"
            src={portStatusCondition[result]}
            alt="-"
            sx={{ width: 24, height: 24 }}
          />
        )}
      </Box>
    </Tooltip>
  );
}


export default getSwitchPortStatus;
